import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

const categorySchema = (cat: typeof categories[0]) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: `${cat.name} - 一起草.com`,
  description: cat.description,
  url: `https://szarssm.cn/category/${cat.slug}`,
});

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = slug ? categoryMap[slug] : undefined;
  const articles = slug ? getArticlesByCategory(slug) : [];

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <section className="py-20 md:py-28 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-6">
                <i className="ri-emotion-unhappy-line text-3xl"></i>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground-950">分类未找到</h1>
              <p className="mt-4 text-foreground-600">抱歉，您查找的分类不存在。</p>
              <Link
                to="/"
                className="inline-flex mt-6 px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOMeta
        title={currentCategory.name}
        description={currentCategory.description}
        keywords={`一起草,${currentCategory.name},在线协作,文稿协作`}
        canonicalUrl={`https://szarssm.cn/category/${currentCategory.slug}`}
        schema={categorySchema(currentCategory)}
      />

      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb & Header */}
        <section className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory.name },
              ]}
            />
            <div className="mt-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${currentCategory.icon} text-xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    共 {currentCategory.articleCount} 篇文章
                  </p>
                </div>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed max-w-3xl">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </section>

        {/* Category Switcher */}
        <section className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles List */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3 flex-wrap">
                        <span className="px-2 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">
                          {currentCategory.name}
                        </span>
                        <span>{article.publishDate}</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug">
                        {article.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="mt-4 flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-secondary-200 text-secondary-600 flex items-center justify-center text-xs font-semibold">
                          {article.authorAvatar}
                        </div>
                        <span className="text-xs text-foreground-500">{article.author}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-file-search-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}